import { isFeatureEnabled } from '../../services/runtime-config';
import { fetchBreadth, fetchIndices } from '../../services/market-data';
import {
  chartRefreshScheduler,
  computeStaleState,
  formatAge,
  FrameBudgetTracker,
  measureFrame,
  RingBuffer,
  getStaleThresholdMs,
  setSnapshot,
  getSnapshot,
  reportChartRenderMetric,
} from '../../charts/engine';
import { renderDonutChart, renderHeatmapGrid, renderLineChart } from '../../charts/primitives';

const teardownMap = new WeakMap<HTMLElement, () => void>();

type BreadthSnapshot = {
    adRatio: number;
    advances: number;
    declines: number;
    unchanged: number;
    indexChanges: number[];
};

  type BreadthPersistedSnapshot = {
    snapshot: BreadthSnapshot;
    adRatios: number[];
    thrustScores: number[];
    pressureScores: number[];
    lastUpdatedAt: number;
  };

export function renderPhase2BreadthEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Breadth</h3>
          <span class="text-xs text-muted">Tier B refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">A/D Ratio Timeline</div>
            <canvas id="v2-breadth-line" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Advance/Decline Donut</div>
            <canvas id="v2-breadth-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Index Change Heatmap</div>
            <canvas id="v2-breadth-heatmap" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth Thrust</div>
            <canvas id="v2-breadth-thrust" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Regime Pressure</div>
            <div id="v2-breadth-pressure" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-breadth-freshness" class="text-xs">Waiting for live feed...</span>
          <span id="v2-breadth-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-breadth-stale" class="v2-stale-overlay" style="display:none;">
          Live feed delayed. Using cached snapshot.
        </div>
      </div>
    `;

    const lineCanvas = container.querySelector('#v2-breadth-line') as HTMLCanvasElement | null;
    const donutCanvas = container.querySelector('#v2-breadth-donut') as HTMLCanvasElement | null;
    const heatmapCanvas = container.querySelector('#v2-breadth-heatmap') as HTMLCanvasElement | null;
    const thrustCanvas = container.querySelector('#v2-breadth-thrust') as HTMLCanvasElement | null;
    const pressureNode = container.querySelector('#v2-breadth-pressure') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-breadth-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-breadth-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-breadth-stale') as HTMLElement | null;

    if (!lineCanvas || !donutCanvas || !heatmapCanvas || !thrustCanvas || !pressureNode || !freshnessNode || !frameNode || !staleNode) return;

    const adRatioSeries = new RingBuffer<number>(120);
    const thrustSeries = new RingBuffer<number>(120);
    const pressureSeries = new RingBuffer<number>(120);
    const frameTracker = new FrameBudgetTracker(200, 16);
    const staleThresholdMs = getStaleThresholdMs('B');
    const snapshotKey = 'phase2:breadth-engine';
    let lastUpdatedAt: number | null = null;
    let lastSnapshot: BreadthSnapshot | null = null;

    const persisted = getSnapshot<BreadthPersistedSnapshot>(snapshotKey);
    if (persisted?.snapshot) {
      persisted.adRatios.forEach(v => adRatioSeries.push(v));
      persisted.thrustScores?.forEach(v => thrustSeries.push(v));
      persisted.pressureScores?.forEach(v => pressureSeries.push(v));
      lastSnapshot = persisted.snapshot;
      lastUpdatedAt = persisted.lastUpdatedAt;
    }

    const computeThrustScore = (snapshot: BreadthSnapshot): number => {
      const denominator = Math.max(1, snapshot.advances + snapshot.declines);
      const breadthEdge = ((snapshot.advances - snapshot.declines) / denominator) * 100;
      return Math.max(-100, Math.min(100, Number(breadthEdge.toFixed(2))));
    };

    const computeRegimePressure = (snapshot: BreadthSnapshot): number => {
      if (snapshot.indexChanges.length === 0) return 0;
      const avgAbsMove = snapshot.indexChanges.reduce((sum, v) => sum + Math.abs(v), 0) / snapshot.indexChanges.length;
      const skewPenalty = snapshot.adRatio < 1 ? (1 - snapshot.adRatio) * 1.5 : 0;
      const pressure = (avgAbsMove * 2.2) + skewPenalty;
      return Math.max(0, Number(pressure.toFixed(2)));
    };

    const draw = (snapshot: BreadthSnapshot) => {
      const sample = measureFrame('breadth-engine', () => {
        renderLineChart(lineCanvas, adRatioSeries.toArray(), '#5aa6ff');
        renderLineChart(thrustCanvas, thrustSeries.toArray(), '#ff9f43');
        renderDonutChart(donutCanvas, [snapshot.advances, snapshot.declines, snapshot.unchanged], ['#15b57a', '#d94736', '#5f6775']);

        const cols = 6;
        const rows = 2;
        const cells = snapshot.indexChanges.slice(0, cols * rows).map((value, idx) => ({
          x: idx % cols,
          y: Math.floor(idx / cols),
          value,
        }));
        renderHeatmapGrid(heatmapCanvas, cells, cols, rows);

        const latestThrust = thrustSeries.toArray().at(-1) || 0;
        const latestPressure = pressureSeries.toArray().at(-1) || 0;
        const thrustTone = latestThrust >= 20 ? 'terminal-text--up' : latestThrust <= -20 ? 'terminal-text--down' : '';
        const pressureTone = latestPressure >= 3 ? 'terminal-text--down' : latestPressure <= 1.2 ? 'terminal-text--up' : '';

        pressureNode.innerHTML = `
          <div class="terminal-list__row"><span>Breadth Thrust</span><strong class="${thrustTone}">${latestThrust >= 0 ? '+' : ''}${latestThrust.toFixed(2)}</strong></div>
          <div class="terminal-list__row"><span>Regime Pressure</span><strong class="${pressureTone}">${latestPressure.toFixed(2)}</strong></div>
          <div class="terminal-list__row"><span>A/D Ratio</span><strong>${snapshot.adRatio.toFixed(2)}</strong></div>
          <div class="terminal-list__row"><span>Advance-Decline Spread</span><strong>${snapshot.advances - snapshot.declines}</strong></div>
        `;
      });

      frameTracker.push(sample.label, sample.durationMs);
      const avg = frameTracker.getAverageMs();
      const within = frameTracker.isWithinBudget(sample.durationMs);
      frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;
      reportChartRenderMetric({
        id: 'phase2-breadth',
        label: 'Breadth Engine',
        latestMs: sample.durationMs,
        averageMs: avg,
        budgetMs: frameTracker.getBudgetMs(),
        withinBudget: within,
      });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const update = async () => {
        try {
            const [breadth, indices] = await Promise.all([fetchBreadth(), fetchIndices()]);
            if (!breadth) throw new Error('breadth unavailable');

            const snapshot: BreadthSnapshot = {
                adRatio: breadth.adRatio,
                advances: breadth.advances,
                declines: breadth.declines,
                unchanged: breadth.unchanged,
                indexChanges: indices.map(i => i.pChange),
            };

            adRatioSeries.push(snapshot.adRatio);
            thrustSeries.push(computeThrustScore(snapshot));
            pressureSeries.push(computeRegimePressure(snapshot));
            lastSnapshot = snapshot;
            lastUpdatedAt = Date.now();
            setSnapshot<BreadthPersistedSnapshot>(snapshotKey, {
              snapshot,
              adRatios: adRatioSeries.toArray(),
              thrustScores: thrustSeries.toArray(),
              pressureScores: pressureSeries.toArray(),
              lastUpdatedAt: lastUpdatedAt,
            });
            draw(snapshot);
        } catch {
            if (lastSnapshot) {
                draw(lastSnapshot);
            }
        }
    };

        if (lastSnapshot) {
          draw(lastSnapshot);
        }

    const taskId = `phase2-breadth-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', update, 3000);

    const cleanup = () => {
        unregister();
    };

    teardownMap.set(container, cleanup);
}
